import React, { useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./CleaningProcess.css";
import useScrollReveal from "../hooks/useScrollReveal.js";
import useSeo, { buildBreadcrumbs } from "../hooks/useSeo.js";

const cleaningProcess = [
  {
    step: "1",
    title: "Fibre & Fabric Inspection",
    icon: "🔍",
    tagline: "The right clean starts with the right diagnosis.",
    description:
      "We inspect the fibre, fabric, stains and condition to choose the safest and most effective treatment.",
  },
  {
    step: "2",
    title: "Industrial Pre-Vacuum",
    icon: "🧹",
    tagline: "Dry dirt out before moisture goes in.",
    description:
      "Our industrial pre-vacuum lifts up to 80% of dry dust, hair, grit and loose debris to help prevent dirt from turning into muddy residue during cleaning.",
  },
  {
    step: "3",
    title: "Targeted Stain Treatment",
    icon: "🎯",
    tagline: "Every stain has a story—and needs its own strategy.",
    description:
      "We pre-treat problem areas, including spills, traffic marks and pet accidents, before the cleaning process begins.",
  },
  {
    step: "4",
    title: "Hot Water Extraction",
    icon: "💨",
    tagline: "Deep clean. Powerful extraction. Visible refresh.",
    description:
      "We pre-apply a cleaning solution to loosen and suspend dirt within the carpet fibres. Hot-water extraction then removes the solution, trapped soil, dirt, allergens, odour and excess moisture ensuring no sticky residues left behind.",
  },
  {
    step: "5",
    title: "Professional Grooming",
    icon: "✨",
    tagline: "The finishing touch that makes the difference.",
    description:
      "Where suitable, we groom the fibres to improve appearance, support even drying and leave a professionally finished look.",
  },
];

const CleaningProcess = () => {
  const pageRef = useRef(null);
  const navigate = useNavigate();
  useScrollReveal(pageRef);
  useSeo({
    title: "Aeti Process – Our Five Steps Professional Carpet Cleaning Process",
    description:
      "See exactly how Aeti Cleaning Services approaches every job: inspection, pre-vacuum, stain treatment, hot water extraction and professional grooming.",
    path: "/cleaning-process",
    structuredData: buildBreadcrumbs([
      { name: "Home", path: "/" },
      { name: "Aeti Process", path: "/cleaning-process" },
    ]),
  });

  return (
    <div className="process-page" ref={pageRef}>
      <section className="process-hero reveal">
        <p className="eyebrow process-hero-eyebrow">Aeti Cleaning Process</p>
        <h1 className="process-hero-title">Our Five Steps Professional Carpet Cleaning Process</h1>
        <p className="process-hero-text">
          Every Aeti carpet, rug, couch, upholstery, mattress, curtain, blind and window cleaning
          service follows a careful, service-specific process—because the right method matters as
          much as the right products.
        </p>
      </section>

      <div className="process-timeline">
        {cleaningProcess.map((item, index) => (
          <article
            className="timeline-step reveal"
            key={item.step}
            style={{ transitionDelay: `${index * 100}ms` }}
          >
            <div className="timeline-marker">
              <span className="timeline-icon">{item.icon}</span>
              <span className="timeline-number">{item.step}</span>
            </div>
            <div className="timeline-content">
              <h2>{item.title}</h2>
              <p className="timeline-tagline">{item.tagline}</p>
              <p>{item.description}</p>
            </div>
          </article>
        ))}
      </div>

      <div className="process-cta reveal">
        <p>Ready to see the difference for yourself?</p>
        <button
          type="button"
          className="primary-btn"
          onClick={() => navigate("/", { state: { scrollToBooking: true } })}
        >
          Book Your Cleaning
        </button>
      </div>
    </div>
  );
};

export default CleaningProcess;
